import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';
// @ts-expect-error
import type {ExecutionResult} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {AlertBox} from 'shells/alert-box/alert-box';
import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {FormField} from 'shells/form/form-field';
import {FormWrapperWithButtons} from 'shells/form/form-wrapper-with-buttons';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';
import {MarketingTitle} from 'shells/typography/marketing-title';

import type {
    GetVerifiedEmailAddresses as GetVerifiedEmailAddressesQuery,
    FromAddressCreate as FromAddressCreateMutation,
    FromAddressCreateVariables as FromAddressCreateMutationVariables,
    GetVerifiedEmailAddresses_fromAddresses_edges_node as EmailAddress,
} from 'nutshell-graphql-types';

import GET_ADDRESSES from '../../select/queries/get-verified-email-addresses.graphql';

import VERIFY_MARKETING_USER_EMAIL from '../graphql/mutations/verify-marketing-user-email.graphql';

type Props = {
    userId?: string;
    subtitle?: string;
    onClose: () => void;
    onSendVerificationEmail?: (fromAddress: EmailAddress) => void;
};

export function VerifySenderAddressModal(props: Props) {
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [senderName, setSenderName] = React.useState<string>('');
    const [email, setEmail] = React.useState<string>('');
    const [isShowingSuccess, setIsShowingSuccess] = React.useState<boolean>(false);
    const [verifyEmail] = useMutation<
        FromAddressCreateMutation,
        FromAddressCreateMutationVariables
    >(VERIFY_MARKETING_USER_EMAIL);

    const handleEnterSubmit = (e: React.KeyboardEvent<any>) => {
        e.preventDefault();
        e.stopPropagation();

        saveAddress(senderName, email);
    };

    const saveAddress = (senderDisplayName?: string | null, emailAddress?: string | null) => {
        if (!senderDisplayName || !emailAddress) {
            return;
        }

        setErrorMessage(null);
        setIsSaving(true);

        verifyEmail({
            variables: {
                input: {
                    emailAddress: {
                        display: senderDisplayName,
                        address: emailAddress,
                    },
                },
            },
            update: (proxy, mutationResult) => {
                const variables = props.userId ? {userId: props.userId} : {};
                const newFromAddress =
                    mutationResult &&
                    mutationResult.data &&
                    mutationResult.data.fromAddressCreate &&
                    mutationResult.data.fromAddressCreate.fromAddress;
                if (!newFromAddress) return;

                const cacheResult: GetVerifiedEmailAddressesQuery | null | undefined =
                    proxy.readQuery({
                        query: GET_ADDRESSES,
                        variables,
                    });

                if (!cacheResult || !cacheResult.fromAddresses) return;

                const existingFromAddresses = cacheResult.fromAddresses;

                proxy.writeQuery({
                    query: GET_ADDRESSES,
                    variables,
                    data: {
                        fromAddresses: {
                            ...existingFromAddresses,
                            edges: [
                                {
                                    cursor: newFromAddress.id,
                                    node: newFromAddress,
                                    __typename: 'FromAddress',
                                },
                            ].concat(existingFromAddresses.edges),
                        },
                    },
                });
            },
        })
            .then((mutationResult: ExecutionResult<FromAddressCreateMutation>) => {
                if (
                    mutationResult.data &&
                    mutationResult.data.fromAddressCreate &&
                    props.onSendVerificationEmail
                ) {
                    props.onSendVerificationEmail(
                        mutationResult.data.fromAddressCreate.fromAddress
                    );
                }

                setIsShowingSuccess(true);
            })
            .catch((err) => {
                setErrorMessage(safelyGetGraphQLErrorMessage(err));
                setIsSaving(false);
            });
    };

    return (
        <AlertBox onClose={props.onClose} trapFocus={{ignoreExternalFocusing: true}}>
            {isShowingSuccess ? (
                <div className='pad-16'>
                    <MarketingTitle>Success!</MarketingTitle>
                    <Body as='p'>
                        We’ve sent an email to <strong>{email}</strong>.
                    </Body>
                    <Body as='p'>
                        Please check your inbox and click the link in the email to finish verifying
                        this sender address.
                    </Body>
                    <div className='flex justify-end'>
                        <ButtonMover direction='right' size='big'>
                            <Button variant='text-primary' size='big' onClick={props.onClose}>
                                Got it
                            </Button>
                        </ButtonMover>
                    </div>
                </div>
            ) : (
                <FormWrapperWithButtons
                    title='Add new sender address'
                    subtitle={
                        props.subtitle || 'Sender name appears to recipients in the “from” field'
                    }
                    isSaving={isSaving}
                    onCancel={props.onClose}
                    buttonText='Send verification email'
                    savingText='Sending…'
                    successText='Sent!'
                    saveButtonDisabled={!senderName || !email}
                    onSaveClick={(e) => {
                        e.preventDefault();

                        saveAddress(senderName, email);
                    }}
                    errorSaving={Boolean(errorMessage)}
                >
                    <FormField innerId='senderName' label='Sender name' isFullWidth={true}>
                        <TextField
                            id='senderName'
                            // @ts-expect-error
                            name='senderName'
                            placeholder='e.g. "Marketing team"'
                            type='text'
                            onEnter={handleEnterSubmit}
                            value={senderName}
                            onChange={setSenderName}
                        />
                    </FormField>
                    <FormField
                        errorMessage={errorMessage}
                        innerId='emailAddress'
                        label='Email address'
                        isFullWidth={true}
                    >
                        <TextField
                            id='emailAddress'
                            // @ts-expect-error
                            name='emailAddress'
                            isFullWidth={true}
                            placeholder='john.appleseed@nutshell.com'
                            type='email'
                            onEnter={handleEnterSubmit}
                            value={email}
                            onChange={setEmail}
                        />
                    </FormField>
                </FormWrapperWithButtons>
            )}
        </AlertBox>
    );
}
